import { styled } from '@mui/material';

export const Page = styled('main')(({ theme }) => ({
  minHeight: 'calc(100vh - 70px)',
  width: '100%',
  padding: '28px 32px 64px',
  boxSizing: 'border-box',
  background: theme.palette.novotalk.$backgroundColor,
  color: theme.palette.novotalk.$textColorDark,
  '@media (max-width: 700px)': {
    padding: '20px 16px 40px',
  },
}));

export const Header = styled('header')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 40,
  minHeight: 54,
  marginBottom: 24,
  '@media (max-width: 760px)': {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 14,
    marginBottom: 20,
  },
});

export const Title = styled('h1')(({ theme }) => ({
  margin: 0,
  color: theme.palette.novotalk.$activeColor,
  fontSize: 38,
  fontWeight: 600,
  lineHeight: '54px',
  letterSpacing: 0,
}));

export const FilterPanel = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'flex-end',
  gap: 16,
  flexWrap: 'wrap',
  '@media (max-width: 700px)': {
    width: '100%',
    justifyContent: 'space-between',
  },
});

export const FilterLabel = styled('span')(({ theme }) => ({
  color: theme.palette.novotalk.$textColorDisabledLight,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

export const FilterControls = styled('div')({
  display: 'grid',
  position: 'relative',
  gap: 8,
  width: 218,
  flexShrink: 0,
});

export const ModeRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  padding: 3,
  border: `1px solid ${theme.palette.novotalk.$inputBorderColor}`,
  borderRadius: 6,
  background: theme.palette.novotalk.$backgroundColor,
}));

export const ModeButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  minHeight: 28,
  padding: '4px 12px',
  border: 0,
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: active ? 600 : 400,
  color: active ? theme.palette.novotalk.$textColorReversed : theme.palette.novotalk.$textColorDarkGray,
  background: active ? theme.palette.novotalk.$activeColor : theme.palette.novotalk.$transparentColor,
  whiteSpace: 'nowrap',
}));

export const UnitPicker = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  zIndex: 20,
  width: '100%',
});

export const UnitSelectButton = styled('button')(({ theme }) => ({
  width: '100%',
  height: 32,
  padding: '0 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.novotalk.$inputBorderColor}`,
  borderRadius: 4,
  background: theme.palette.novotalk.$backgroundColor,
  color: theme.palette.novotalk.$textColorDarkGray,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 14,
  textAlign: 'left',
  '& svg': {
    color: theme.palette.novotalk.$textColorDisabledLight,
    fontSize: 18,
  },
}));

export const UnitMenu = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 40,
  right: 0,
  width: 320,
  borderRadius: 4,
  background: theme.palette.novotalk.$backgroundColor,
  boxShadow: '0 4px 18px rgba(30, 40, 60, 0.14)',
  overflow: 'hidden',
}));

export const UnitSearch = styled('label')(({ theme }) => ({
  height: 50,
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  boxSizing: 'border-box',
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  '& svg': {
    marginLeft: 2,
    color: theme.palette.novotalk.$textColorDisabledLight,
    fontSize: 22,
  },
  '& input': {
    width: '100%',
    height: 38,
    padding: '0 10px 0 0',
    border: 0,
    outline: 0,
    color: theme.palette.novotalk.$textColorDark,
    background: theme.palette.novotalk.$transparentColor,
    font: 'inherit',
    fontSize: 15,
  },
}));

export const UnitOptions = styled('div')({
  maxHeight: 276,
  padding: '8px 0',
  overflowY: 'auto',
});

export const UnitOption = styled('label')(({ theme }) => ({
  minHeight: 40,
  padding: '7px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  boxSizing: 'border-box',
  color: theme.palette.novotalk.$textColorDark,
  cursor: 'pointer',
  fontSize: 15,
  '&:hover': { background: theme.palette.novotalk.$backgroundColorMedium },
  '& .MuiCheckbox-root': {
    padding: 0,
    color: theme.palette.novotalk.$textColorDisabledLight,
  },
  '& .Mui-checked': { color: `${theme.palette.novotalk.$activeColor} !important` },
}));

export const UnitName = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TestBadge = styled('span')(({ theme }) => ({
  marginLeft: 'auto',
  padding: '3px 10px',
  borderRadius: 14,
  background: theme.palette.novotalk.$backgroundColorMedium,
  color: theme.palette.novotalk.$textColorDisabledLight,
  fontSize: 12,
  fontWeight: 600,
}));

export const UnitMenuFooter = styled('div')(({ theme }) => ({
  minHeight: 66,
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  borderTop: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
}));

export const UnitMenuActions = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2px 12px',
});

export const UnitMenuAction = styled('button')(({ theme }) => ({
  padding: 0,
  border: 0,
  background: theme.palette.novotalk.$transparentColor,
  color: theme.palette.novotalk.$activeColor,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 12,
  fontWeight: 600,
}));

export const UnitMenuDone = styled('button')(({ theme }) => ({
  height: 32,
  padding: '6px 16px',
  border: 0,
  borderRadius: 4,
  background: theme.palette.novotalk.$activeColor,
  color: theme.palette.novotalk.$textColorReversed,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 600,
}));

export const Layout = styled('div')({
  display: 'grid',
  gridTemplateColumns: '84px minmax(0, 1fr)',
  gap: 20,
  '@media (max-width: 700px)': {
    gridTemplateColumns: '72px minmax(0, 1fr)',
    gap: 12,
  },
});

export const Tabs = styled('nav')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

export const Tab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  width: 84,
  minHeight: 64,
  padding: '10px 6px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: 0,
  borderRadius: 6,
  background: active ? theme.palette.novotalk.$selectEnabledBgColorHover : theme.palette.novotalk.$transparentColor,
  color: active ? theme.palette.novotalk.$activeColor : theme.palette.novotalk.$textColorDarkGray,
  fontSize: 13,
  lineHeight: '16px',
  fontWeight: active ? 600 : 400,
  textAlign: 'center',
  cursor: 'pointer',
  '& svg': { fontSize: 28 },
  '@media (max-width: 700px)': {
    width: 72,
    padding: '8px 4px',
    fontSize: 12,
  },
}));

export const Content = styled('section')({ minWidth: 0, padding: '6px 0 24px' });

export const SectionTitle = styled('h2')(({ theme }) => ({
  margin: '0 0 14px',
  color: theme.palette.novotalk.$navigationHelpIconColor,
  fontSize: 23,
  lineHeight: '30px',
  fontWeight: 600,
  letterSpacing: 0,
}));

export const MetricGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 16,
  marginBottom: 16,
  '@media (max-width: 1180px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  '@media (max-width: 700px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
});

export const PatientsMetricGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 16,
  marginBottom: 16,
  '@media (max-width: 1180px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  '@media (max-width: 700px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
});

export const Card = styled('article', {
  shouldForwardProp: (prop) => !['compact', 'patient', 'therapist'].includes(prop),
})(({
  theme, compact, patient, therapist,
}) => ({
  width: therapist ? 360 : 'auto',
  maxWidth: '100%',
  height: therapist ? 200 : patient ? 210 : compact ? 140 : 158,
  padding: compact ? '20px 22px' : '22px 24px',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  borderRadius: 8,
  background: theme.palette.novotalk.$backgroundColor,
  ...(therapist ? { marginBottom: 16 } : {}),
}));

export const CardHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
});

export const CardLabel = styled('h3')(({ theme }) => ({
  margin: 0, color: theme.palette.novotalk.$textColorDisabledLight, fontSize: 17, lineHeight: '24px', fontWeight: 600, letterSpacing: 0,
}));
export const CardPeriod = styled('span')(({ theme }) => ({
  padding: '4px 12px', borderRadius: 18, color: theme.palette.novotalk.$textColorDarkGray, background: theme.palette.novotalk.$backgroundColorMedium, fontSize: 12, lineHeight: '20px', fontWeight: 600, whiteSpace: 'nowrap',
}));
export const CardValue = styled('div')(({ theme }) => ({
  margin: '10px 0 7px', color: theme.palette.novotalk.$textColorDark, fontSize: 40, lineHeight: '48px', fontWeight: 600,
}));
export const CardHelp = styled('p')(({ theme }) => ({
  margin: 'auto 0 0', color: theme.palette.novotalk.$textColorDisabledLight, fontSize: 14, lineHeight: '20px',
}));
export const ValueSuffix = styled('span')(({ theme }) => ({
  marginLeft: 5, color: theme.palette.novotalk.$textColorDarkGray, fontSize: 14, fontWeight: 400,
}));
export const DetailSubtitle = styled('p')(({ theme }) => ({
  margin: '2px 0 10px', color: theme.palette.novotalk.$textColorDisabledLight, fontSize: 14, lineHeight: '20px',
}));

export const DetailGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
  gap: 20,
  marginBottom: 16,
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
});

export const DetailCard = styled(Card, {
  shouldForwardProp: (prop) => !['fullWidth', 'roleCard', 'patientBreakdown'].includes(prop),
})(({ fullWidth, roleCard, patientBreakdown }) => ({
  width: fullWidth ? '100%' : roleCard ? 640 : 'auto',
  maxWidth: '100%',
  height: patientBreakdown ? 353 : fullWidth ? 244 : 232,
  marginBottom: 16,
  '@media (max-width: 700px)': {
    width: '100%',
    height: 'auto',
    minHeight: patientBreakdown ? 353 : 232,
  },
}));

export const CourseCard = styled(DetailCard)({
  overflow: 'hidden',
});

export const CourseBody = styled('div')({
  minHeight: 0,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 28,
  marginTop: 10,
  overflow: 'hidden',
  '@media (max-width: 700px)': {
    flexWrap: 'wrap',
    justifyContent: 'center',
    overflow: 'visible',
  },
});

export const CoursePie = styled('div')({
  width: 150,
  height: 150,
  flex: '0 0 auto',
  '& canvas': {
    width: '150px !important',
    height: '150px !important',
  },
});

export const CourseLegend = styled('div')({
  minWidth: 150,
  minHeight: 0,
  maxHeight: 160,
  flex: '1 1 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  overflowY: 'auto',
  paddingRight: 4,
});

export const CourseLegendRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '12px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: 10,
  minHeight: 28,
  paddingBottom: 8,
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  fontSize: 15,
}));

export const CourseSwatch = styled('span', {
  shouldForwardProp: (prop) => prop !== 'chartColor',
})(({ chartColor }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: chartColor,
}));

export const CourseLabel = styled('span')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'baseline',
  gap: 5,
});

export const CourseName = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const CourseCount = styled('span')(({ theme }) => ({
  flex: '0 0 auto',
  color: theme.palette.novotalk.$textColorDisabledLight,
}));

export const BarRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(112px, 1fr) 2fr 54px 54px',
  alignItems: 'center',
  gap: 12,
  marginTop: 13,
  color: theme.palette.novotalk.$textColorDarkGray,
  fontSize: 13,
  lineHeight: '18px',
  '@media (max-width: 700px)': {
    gridTemplateColumns: 'minmax(74px, 1fr) minmax(30px, 1fr) 38px 38px',
    gap: 5,
    fontSize: 11,
  },
}));
export const BarTrack = styled('div')(({ theme }) => ({
  height: 8, overflow: 'hidden', borderRadius: 4, background: theme.palette.novotalk.$borderColorLightBlue,
}));
export const BarFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ theme, width }) => ({ width: `${width}%`, height: '100%', background: theme.palette.novotalk.$activeColor }));

export const Percentage = styled('span')(({ theme }) => ({ color: theme.palette.novotalk.$textColorDark, fontWeight: 600, textAlign: 'right' }));
export const Count = styled('span')(({ theme }) => ({ color: theme.palette.novotalk.$textColorDarkGray, textAlign: 'right' }));

export const PeriodControl = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 6,
  '& .MuiInput-root': {
    minWidth: 90,
    height: 28,
    padding: '0 10px',
    borderRadius: 18,
    color: theme.palette.novotalk.$activeColor,
    background: theme.palette.novotalk.$selectEnabledBgColorHover,
    fontSize: 12,
    fontWeight: 600,
  },
  '& .MuiInput-root:before, & .MuiInput-root:after': { display: 'none' },
  '& .MuiSelect-select': { padding: '4px 22px 4px 2px !important' },
  '& .MuiSelect-icon': { right: 2, color: theme.palette.novotalk.$activeColor, fontSize: 18 },
}));

export const CustomDates = styled('div')({
  display: 'flex',
  gap: 6,
  '& .MuiInputBase-root': { height: 30, fontSize: 11 },
  '& input': { width: 92, padding: '5px 7px' },
});
